import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import mapboxgl from "mapbox-gl";
import * as React from "react";
import DataTable from "react-data-table-component";
import { TripsReportsRow } from "../../../../api";
import {
    HeadCell,
    SortableTable,
    newDisplayCond,
} from "../../../../components/table/sortableTable";
import { readyNeighborData } from "../../../../services/weekly/map";
import {
    getGoCancels,
    getNeighborsPerTrip,
    selectTripDetails,
    turnNeighborSwitchOff,
} from "../../../../services/weekly/weeklyreport";
import { colors } from "../../../../theme";
import {
    convertBoardingType,
    convertUTCtoJST,
    secToMinAndSec,
} from "../../../../utils/formatting";
import { convertUTCtoTimestamp } from "../../../../utils/time";
import { TripDetails } from "../store";

const convertToTripTableData = (
    trips: TripsReportsRow[],
    worst_trip_id: string | null
): TripsTableRow[] => {
    const _data = trips.map((rec: TripsReportsRow) => {
        let preSelected = "no";

        if (worst_trip_id == rec.tripSessionId) {
            preSelected = "yes";
        }
        return {
            unladen_start_tstamp: convertUTCtoTimestamp(rec.unladenStartTs),
            unladen_start_ts: rec.unladenStartTs,
            unladen_stop_ts: rec.boardingTs,
            unladen_sec: rec.unladenSec,
            ride_type: rec.boardingType,
            deviation_count: rec.deviationCount,
            navi_off_sec: rec.naviOffSec,
            go_cancel_count: rec.goCancelCount,
            selected: preSelected,
            trip_id: rec.tripSessionId,
            id: `${rec.tripSessionId}_${rec.boardingType}_row`,
        } as TripsTableRow;
    });

    return _data;
};

const showSelectedTrip = (
    trips: TripsReportsRow[],
    selected_trip_id: string | null
) => {
    const _filtered = trips.filter((x: TripsReportsRow) => {
        return x.tripSessionId == selected_trip_id;
    });
    const _data = _filtered.map((rec: TripsReportsRow) => {
        return {
            unladen_start_tstamp: convertUTCtoTimestamp(rec.unladenStartTs),
            unladen_start_ts: rec.unladenStartTs,
            unladen_stop_ts: rec.boardingTs,
            unladen_sec: rec.unladenSec,
            ride_type: rec.boardingType,
            deviation_count: rec.deviationCount,
            navi_off_sec: rec.naviOffSec,
            go_cancel_count: rec.goCancelCount,
            selected: "no",
            trip_id: rec.tripSessionId,
            id: `${rec.tripSessionId}_${rec.boardingType}_row`,
        } as TripsTableRow;
    });

    return _data;
};

type TripsTableRow = {
    selected: string;
    trip_id: string;
    unladen_start_tstamp: number;
    unladen_start_ts: string;
    unladen_stop_ts: string;
    unladen_sec: number;
    ride_type: string;
    deviation_count: number;
    navi_off_sec: number;
    go_cancel_count: number;
    id: string;
};

export const headCells: HeadCell[] = [
    {
        label: "空車開始-空車終了（空車時間）",
        id: "unladen_start_ts",
        grow: 2,
        wrap: true,
        numeric: true,
        headerAlignRight: true,
        cell: function DC(x: TripsTableRow): JSX.Element {
            return (
                <Box>
                    {`${convertUTCtoJST(x.unladen_start_ts)}-${convertUTCtoJST(
                        x.unladen_stop_ts
                    )} (${secToMinAndSec(x.unladen_sec)})`}
                </Box>
            );
        },
    },
    {
        label: "乗車種別",
        id: "ride_type",
        numeric: false,
        grow: 1,
        wrap: true,
        format: (x: string): string => {
            return convertBoardingType(x);
        },
    },
    {
        label: "離脱回数",
        id: "deviation_count",
        numeric: true,
        grow: 1,
        align: "right",
        wrap: true,
        headerAlignRight: true,
        format: (x: string): string => {
            const ct = parseInt(x, 10) ? x : "0";
            return `${ct}回`;
        },
    },
    {
        label: "非利用時間",
        id: "navi_off_sec",
        numeric: true,
        grow: 1,
        align: "right",
        wrap: true,
        headerAlignRight: true,
        format: (x: number): string => {
            return secToMinAndSec(x);
        },
    },
    {
        label: "GOキャンセル回数",
        id: "go_cancel_count",
        numeric: true,
        grow: 1,
        align: "right",
        wrap: true,
        headerAlignRight: true,
        format: (x: string): string => {
            const ct = parseInt(x, 10) ? x : "0";
            return `${ct}回`;
        },
    },
];

export const tripsTableCols = [
    {
        name: "空車開始-空車終了（空車時間）",
        selector: "unladen_start_ts",
        grow: 2,
        wrap: true,
        right: true,
        sortable: true,
        format: (x: TripsTableRow): string => {
            return `${convertUTCtoJST(x.unladen_start_ts)}-${convertUTCtoJST(
                x.unladen_stop_ts
            )} (${secToMinAndSec(x.unladen_sec)})`;
        },
    },
    {
        name: "乗車種別",
        selector: "ride_type",
        sortable: true,
        grow: 1,
        wrap: true,
        format: (x: TripsTableRow): string => {
            return convertBoardingType(x.ride_type);
        },
    },
    {
        name: "離脱回数",
        selector: "deviation_count",
        sortable: true,
        grow: 1,
        right: true,
        wrap: true,
        format: (x: TripsTableRow): string => {
            const ct = x.deviation_count ?? "0";
            return `${ct}回`;
        },
    },
    {
        name: "非利用時間",
        selector: "navi_off_sec",
        sortable: true,
        grow: 1,
        right: true,
        wrap: true,
        format: (x: TripsTableRow): string => {
            return secToMinAndSec(x.navi_off_sec);
        },
    },
    {
        name: "GOキャンセル回数",
        selector: "go_cancel_count",
        sortable: true,
        grow: 1,
        right: true,
        wrap: true,
        format: (x: TripsTableRow): string => {
            const ct = x.go_cancel_count ?? "0";
            return `${ct}回`;
        },
    },
];

interface TripsTableProps {
    trips: TripsReportsRow[] | null;
    tripDetails: TripDetails | null;
    shiftId: string | null;
    map: mapboxgl.Map;
}

interface DataProps {
    data: any;
    updateReason: string;
}

// Printable mini version of the TripsTable
// shows only focused trip
// keep DataTable element for this b/c small and simple
const MiniTripsTable: React.FC<TripsTableProps> = (props: TripsTableProps) => {
    if (!props.trips) {
        return <Box id="MiniTripsTable-container"></Box>;
    }
    const tripId = props.tripDetails ? props.tripDetails.trip_id : "";
    const _data = showSelectedTrip(props.trips, tripId);
    const thisMapShift =
        props.map && props.map.getContainer()
            ? (props.map.getContainer() as HTMLDivElement).id
            : "noid";

    console.debug("Mini Trips Table:", _data, props, thisMapShift);
    if (!_data || _data.length == 0) {
        return <Box></Box>;
    }
    return (
        <Box>
            <DataTable<TripsTableRow>
                className="row-border"
                noHeader={true}
                columns={tripsTableCols}
                data={_data}
            />
        </Box>
    );
};
export class TripsTable extends React.Component<TripsTableProps, DataProps> {
    constructor(props: any) {
        super(props);
        this.state = {
            data: [],
            updateReason: "init",
        };
    }

    componentDidUpdate = async () => {
        // scroll only on init, not click
        if (this.state.updateReason == "init") {
            if (!this.props.trips) {
                return;
            }
            const tripId = this.props.tripDetails
                ? this.props.tripDetails.trip_id
                : "";
            const _data = convertToTripTableData(this.props.trips, tripId);
            const ixSelected = this.getSelectedRow(_data);
            this.gotoRow(ixSelected);
        }
    };

    tblcontainer;

    gotoRow = (ix: number) => {
        const tbc = this.tblcontainer as HTMLDivElement;
        if (!tbc) {
            return;
        }
        const par = tbc.parentElement;
        if (!par) {
            return;
        }
        par.scrollTo(0, 0);
        const row_height = 46.74;
        par.scrollBy(0, row_height * ix);
    };

    getSelectedRow = (_data: TripsTableRow[]): number => {
        _data.sort(function (a: TripsTableRow, b: TripsTableRow) {
            return a.unladen_start_tstamp - b.unladen_start_tstamp;
        });

        let ix_selected = 0;
        for (let i = 0; i < _data.length; i++) {
            const row = _data[i];
            if (row.selected == "yes") {
                ix_selected = i;
            }
        }
        return ix_selected;
    };

    render = () => {
        if (!this.props.trips) {
            return (
                <Box
                    id="TripsTable-container"
                    ref={(el) => (this.tblcontainer = el)}
                ></Box>
            );
        }

        const tripId = this.props.tripDetails
            ? this.props.tripDetails.trip_id
            : "";

        const _data = convertToTripTableData(this.props.trips, tripId);

        const handleRowClicked = async (row: TripsTableRow) => {
            await turnNeighborSwitchOff();
            const updatedData = _data.map((x) => {
                if (row.id !== x.id) {
                    return {
                        ...x,
                        selected: "no",
                    };
                }

                return {
                    ...x,
                    selected: "yes",
                };
            });

            this.setState({ data: updatedData });
            this.setState({ updateReason: "click" });
            await selectTripDetails(row.trip_id, row.unladen_start_ts);
            getGoCancels();
            const nb = await getNeighborsPerTrip();
            readyNeighborData(this.props.map, nb);
        };

        return (
            <Box
                id={`${this.props.shiftId}_tbl`}
                sx={{ width: "100%" }}
                ref={(el) => (this.tblcontainer = el)}
            >
                <SortableTable
                    tableHeaders={headCells}
                    tableData={_data}
                    tableProps={this.props}
                    displayCond={newDisplayCond(
                        "unladen_start_ts",
                        "asc",
                        0,
                        20
                    )}
                    rowIdField={"trip_id"}
                    onRowClickMethod={handleRowClicked}
                    changeRowColorOnSelect
                    // slimRows={true}
                />
            </Box>
        );
    };
}

interface RideTypeProps {
    nr_cruise_rides: number;
    nr_go_rides: number;
    nr_tel_rides: number;
    nr_stand_rides: number;
    trips: TripsReportsRow[] | null;
    tripDetails: TripDetails | null;
    shiftId: string | null;
    map: mapboxgl.Map;
}

export const TripsAndRideInfo: React.FC<RideTypeProps> = (
    props: RideTypeProps
) => {
    const borderStyle = `1px solid ${colors.gray.a50}`;

    return (
        <Box
            id="ridetypes-display"
            sx={{
                zIndex: 2000,
                left: "16px",
                bottom: "20px",
                position: "absolute",
                display: "flex",
                flexDirection: "column",
                width: "93%",
            }}
        >
            <Box
                sx={{
                    displayPrint: "none",
                }}
            >
                <Box
                    sx={{
                        backgroundColor: colors.white.main,
                        borderRadius: "4px",
                        boxShadow: "0px 1px 4px rgb(0 0 0 / 20%)",
                        mb: 4,
                        width: "fit-content",
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <Box
                        sx={{
                            p: 4,
                        }}
                    >
                        <Typography variant="h4">乗車種別</Typography>
                    </Box>
                    <Box
                        sx={{
                            p: 1,
                            pr: 4,
                            pl: 4,
                            borderLeft: borderStyle,
                        }}
                    >
                        流し　{props.nr_cruise_rides}回
                    </Box>
                    <Box
                        sx={{
                            borderLeft: borderStyle,
                            p: 1,
                            pr: 4,
                            pl: 4,
                        }}
                    >
                        GO {props.nr_go_rides}回
                    </Box>
                    <Box
                        sx={{
                            borderLeft: borderStyle,
                            p: 1,
                            pr: 4,
                            pl: 4,
                        }}
                    >
                        無線　{props.nr_tel_rides}回
                    </Box>
                    <Box
                        sx={{
                            borderLeft: borderStyle,
                            p: 1,
                            pr: 4,
                            pl: 4,
                        }}
                    >
                        乗り場　{props.nr_stand_rides}回
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    displayPrint: "none",
                }}
            >
                <Box
                    id="trips-table"
                    sx={{
                        backgroundColor: colors.white.main,
                        borderRadius: "4px",
                        boxShadow: "0px 1px 4px rgb(0 0 0 / 20%)",
                        pt: 2,
                        pb: 2,
                        height: "150px",
                        overflow: "auto",
                    }}
                >
                    <TripsTable
                        trips={props.trips}
                        tripDetails={props.tripDetails}
                        shiftId={props.shiftId}
                        map={props.map}
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    display: "none",
                    displayPrint: "block",
                    bottom: "-10px",
                    position: "absolute",
                    width: "93%",
                    left: "16px",
                }}
            >
                <Box>
                    <MiniTripsTable
                        trips={props.trips}
                        tripDetails={props.tripDetails}
                        shiftId={props.shiftId}
                        map={props.map}
                    />
                </Box>
            </Box>
        </Box>
    );
};
